<template>
  <v-card outlined class="mb-4">
    <v-card-text>
      <v-alert type="info" outlined class="mb-4">
        <v-icon left>mdi-information</v-icon>
        Pregledajte sve podatke prije potvrde proizvodnje po normativu
      </v-alert>

      <!-- Product Summary -->
      <v-card outlined color="success lighten-5" class="mb-4">
        <v-card-title class="subtitle-1 success white--text">
          <v-icon left color="white">mdi-package-variant-closed</v-icon>
          Proizvod
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="4">
              <v-card outlined>
                <v-card-text class="text-center">
                  <div class="caption grey--text">Naziv</div>
                  <div class="text-h6">{{ product ? product.name : 'N/A' }}</div>
                  <div class="caption">{{ product ? product.code : '' }}</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="4">
              <v-card outlined color="success lighten-4">
                <v-card-text class="text-center">
                  <div class="caption grey--text">Količina</div>
                  <div class="text-h6 success--text">{{ desiredQuantity }} kg</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="4">
              <v-card outlined>
                <v-card-text class="text-center">
                  <div class="caption grey--text">Odredišno skladište</div>
                  <div class="text-h6">{{ outputWarehouse ? outputWarehouse.name : 'N/A' }}</div>
                  <div class="caption">{{ outputWarehouse ? outputWarehouse.location : '' }}</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Ingredients from Normative -->
      <v-card outlined class="mb-4">
        <v-card-title class="subtitle-1 primary white--text">
          <v-icon left color="white">mdi-format-list-checks</v-icon>
          Sastojci iz normativa
        </v-card-title>
        <v-card-text>
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Sastojak</th>
                  <th class="text-right">Po jedinici</th>
                  <th class="text-right">Ukupna količina</th>
                  <th>Skladište</th>
                  <th class="text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(ingredient, index) in normativeIngredients"
                  :key="index"
                  :class="ingredient.quantity !== ingredient.originalQuantity ? 'warning lighten-5' : ''"
                >
                  <td>
                    <div>{{ ingredient.name }}</div>
                    <v-chip x-small color="primary" class="mt-1">Normativ</v-chip>
                  </td>
                  <td class="text-right">
                    {{ ingredient.quantityPerUnit ? ingredient.quantityPerUnit.toFixed(3) : '0' }} kg
                  </td>
                  <td class="text-right">
                    <strong>{{ ingredient.quantity.toFixed(2) }} kg</strong>
                    <div v-if="ingredient.quantity !== ingredient.originalQuantity" class="caption warning--text">
                      Promijenjena količina
                    </div>
                  </td>
                  <td>
                    <div>{{ ingredient.warehouse ? ingredient.warehouse.name : 'N/A' }}</div>
                    <div class="caption grey--text">
                      {{ ingredient.warehouse ? ingredient.warehouse.location : '' }}
                    </div>
                  </td>
                  <td class="text-right">
                    <v-chip
                      small
                      :color="isAvailable(ingredient) ? 'success' : 'error'"
                      text-color="white"
                    >
                      {{ isAvailable(ingredient) ? 'Dostupno' : 'Nedostaje' }}
                    </v-chip>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>

      <!-- Additional Ingredients -->
      <v-card outlined class="mb-4" v-if="additionalIngredients.length > 0">
        <v-card-title class="subtitle-1 orange white--text">
          <v-icon left color="white">mdi-plus-circle</v-icon>
          Dodatni sastojci (izvan normativa)
        </v-card-title>
        <v-card-text>
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Sastojak</th>
                  <th class="text-right">Količina</th>
                  <th>Skladište</th>
                  <th class="text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(ingredient, index) in additionalIngredients" :key="index">
                  <td>
                    <div>{{ ingredient.name }}</div>
                    <v-chip x-small color="orange" class="mt-1">Dodatno</v-chip>
                  </td>
                  <td class="text-right">
                    <strong>{{ ingredient.quantity.toFixed(2) }} kg</strong>
                  </td>
                  <td>
                    <div>{{ ingredient.warehouse ? ingredient.warehouse.name : 'N/A' }}</div>
                    <div class="caption grey--text">
                      {{ ingredient.warehouse ? ingredient.warehouse.location : '' }}
                    </div>
                  </td>
                  <td class="text-right">
                    <v-chip
                      small
                      :color="isAvailable(ingredient) ? 'success' : 'error'"
                      text-color="white"
                    >
                      {{ isAvailable(ingredient) ? 'Dostupno' : 'Nedostaje' }}
                    </v-chip>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>

      <!-- Cost Analysis -->
      <v-card outlined color="blue lighten-5">
        <v-card-title class="subtitle-1">
          <v-icon left>mdi-calculator</v-icon>
          Analiza proizvodnje
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="3">
              <v-card outlined>
                <v-card-text class="text-center">
                  <div class="caption grey--text">Ukupna težina sirovine</div>
                  <div class="text-h6">{{ totalInputWeight.toFixed(2) }} kg</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="3">
              <v-card outlined>
                <v-card-text class="text-center">
                  <div class="caption grey--text">Proizvod</div>
                  <div class="text-h6 success--text">{{ desiredQuantity }} kg</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="3">
              <v-card outlined>
                <v-card-text class="text-center">
                  <div class="caption grey--text">Sastojaka iz normativa</div>
                  <div class="text-h6 primary--text">{{ normativeIngredients.length }}</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="3">
              <v-card outlined>
                <v-card-text class="text-center">
                  <div class="caption grey--text">Dodatnih sastojaka</div>
                  <div class="text-h6 orange--text">{{ additionalIngredients.length }}</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-divider class="my-3"></v-divider>

          <v-row>
            <v-col cols="12">
              <v-alert
                :type="totalInputWeight > desiredQuantity ? 'warning' : 'info'"
                dense
                text
              >
                <v-icon left>mdi-information</v-icon>
                <span v-if="totalInputWeight > desiredQuantity">
                  <strong>Upozorenje:</strong> Ukupna težina sirovine ({{ totalInputWeight.toFixed(2) }} kg) je veća od
                  željene količine proizvoda ({{ desiredQuantity }} kg).
                  Razlika: {{ (totalInputWeight - desiredQuantity).toFixed(2) }} kg
                </span>
                <span v-else-if="totalInputWeight < desiredQuantity">
                  <strong>Informacija:</strong> Ukupna težina sirovine ({{ totalInputWeight.toFixed(2) }} kg) je manja od
                  željene količine proizvoda ({{ desiredQuantity }} kg).
                  Razlika: {{ (desiredQuantity - totalInputWeight).toFixed(2) }} kg
                </span>
                <span v-else>
                  <strong>Perfektno:</strong> Ukupna težina sirovine odgovara željenoj količini proizvoda.
                </span>
              </v-alert>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'ProductionConfirmation',
  props: {
    product: {
      type: Object,
      default: () => null
    },
    outputWarehouse: {
      type: Object,
      default: () => null
    },
    desiredQuantity: {
      type: Number,
      default: 0
    },
    ingredients: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    normativeIngredients () {
      return this.ingredients.filter(ing => ing.fromNormative)
    },
    additionalIngredients () {
      return this.ingredients.filter(ing => !ing.fromNormative)
    },
    totalInputWeight () {
      return this.ingredients.reduce((sum, ing) => sum + (ing.quantity || 0), 0)
    },
    totalCost () {
      return this.ingredients.reduce((sum, ing) => {
        return sum + ((ing.quantity || 0) * (ing.pricePerKg || 0))
      }, 0)
    },
    costPerKg () {
      if (!this.desiredQuantity) return 0
      return this.totalCost / this.desiredQuantity
    }
  },
  methods: {
    isAvailable (ingredient) {
      return ingredient.quantity <= ingredient.availableStock
    }
  }
}
</script>

<style scoped>
.v-card {
  transition: all 0.3s;
}
</style>
